import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getMyBookings, updateBookingStatus, getMyHomeBookings } from "../utils/api";
import ReviewModal from "../components/ReviewModal";
import Toast from "../components/Toast";

export default function MyBookings() {
  const navigate = useNavigate();

  // Salon bookings
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [reviewBooking, setReviewBooking] = useState(null);
  const [reviewedIds, setReviewedIds] = useState([]);

  // Home bookings
  const [homeBookings, setHomeBookings] = useState([]);
  const [homeLoading, setHomeLoading] = useState(true);
  const [homeFilter, setHomeFilter] = useState("all");
  const [reviewedHomeIds, setReviewedHomeIds] = useState([]);

  // Shared
  const [activeTab, setActiveTab] = useState("salon");
  const [toast, setToast] = useState({ message: "", type: "success" });
  const showToast = (message, type = "success") => setToast({ message, type });

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const res = await getMyBookings();
        setBookings(res.data);
      } catch { setError("Could not load salon bookings."); }
      finally { setLoading(false); }
    };
    const fetchHomeBookings = async () => {
      try {
        const res = await getMyHomeBookings();
        setHomeBookings(Array.isArray(res.data.bookings) ? res.data.bookings : []);
      } catch { /* silent */ }
      finally { setHomeLoading(false); }
    };
    fetchBookings();
    fetchHomeBookings();
  }, []);

  const handleCancel = async (bookingId) => {
    try {
      await updateBookingStatus(bookingId, "cancelled");
      setBookings((prev) => prev.map((b) => b.id === bookingId ? { ...b, status: "cancelled" } : b));
      showToast("Appointment cancelled.", "info");
    } catch { showToast("Could not cancel booking.", "error"); }
  };

  // Salon review success
  const handleReviewSuccess = () => {
    setReviewedIds((prev) => [...prev, reviewBooking.id]);
    setReviewBooking(null);
    showToast("Review submitted! Thank you! ⭐", "success");
  };

  // Home review success
  const handleHomeReviewSuccess = () => {
    setReviewedHomeIds((prev) => [...prev, reviewBooking.id]);
    setReviewBooking(null);
    showToast("Review submitted! Thank you! ⭐", "success");
  };

  // Determine which success handler to use based on booking type
  const handleReviewSuccessRouter = () => {
    if (reviewBooking?.service_names) {
      handleHomeReviewSuccess();
    } else {
      handleReviewSuccess();
    }
  };

  // ── Salon filters ──
  const filteredBookings = bookings.filter((b) => {
    if (activeFilter === "all") return true;
    if (activeFilter === "upcoming") return ["pending", "confirmed"].includes(b.status);
    return b.status === activeFilter;
  });

  const counts = {
    all: bookings.length,
    upcoming: bookings.filter((b) => ["pending", "confirmed"].includes(b.status)).length,
    completed: bookings.filter((b) => b.status === "completed").length,
    cancelled: bookings.filter((b) => b.status === "cancelled").length,
  };

  // ── Home filters ──
  const filteredHomeBookings = homeBookings.filter((b) => {
    if (homeFilter === "all") return true;
    if (homeFilter === "upcoming") return ["pending", "confirmed"].includes(b.status);
    return b.status === homeFilter;
  });

  const homeCounts = {
    all: homeBookings.length,
    upcoming: homeBookings.filter((b) => ["pending", "confirmed"].includes(b.status)).length,
    completed: homeBookings.filter((b) => b.status === "completed").length,
  };

  return (
    <div style={styles.page}>
      {reviewBooking && (
        <ReviewModal
          booking={reviewBooking}
          onClose={() => setReviewBooking(null)}
          onSuccess={handleReviewSuccessRouter}
        />
      )}

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerInner}>
          <h1 style={styles.title}>📅 My Bookings</h1>
          <button onClick={() => navigate("/shops")} style={styles.newBtn}>+ New Booking</button>
        </div>
      </div>

      {/* Tab Toggle */}
      <div style={styles.tabBar}>
        <button
          onClick={() => setActiveTab("salon")}
          style={{ ...styles.tabBtn, ...(activeTab === "salon" ? styles.tabBtnActiveSalon : {}) }}
        >
          💈 Salon Bookings
          {counts.upcoming > 0 && <span style={styles.tabBadgeRed}>{counts.upcoming}</span>}
        </button>
        <button
          onClick={() => setActiveTab("home")}
          style={{ ...styles.tabBtn, ...(activeTab === "home" ? styles.tabBtnActiveHome : {}) }}
        >
          🏠 Home Bookings
          {homeCounts.upcoming > 0 && <span style={styles.tabBadgeGreen}>{homeCounts.upcoming}</span>}
        </button>
      </div>

      <div style={styles.container}>
        {error && <div style={styles.errorBox}>{error}</div>}

        {/* ── SALON TAB ── */}
        {activeTab === "salon" && (
          <>
            {loading ? (
              <p style={{ color: "var(--text-muted)", textAlign: "center", padding: "40px" }}>Loading...</p>
            ) : (
              <>
                <div style={styles.statsRow}>
                  <StatCard label="Total" value={counts.all} color="var(--accent)" />
                  <StatCard label="Upcoming" value={counts.upcoming} color="#4fc3f7" />
                  <StatCard label="Completed" value={counts.completed} color="#4caf50" />
                  <StatCard label="Cancelled" value={counts.cancelled} color="#ff9800" />
                </div>
                <div style={styles.filterRow}>
                  {["all", "upcoming", "completed", "cancelled"].map((filter) => (
                    <button key={filter} onClick={() => setActiveFilter(filter)}
                      style={{ ...styles.filterBtn, ...(activeFilter === filter ? styles.filterBtnActiveSalon : {}) }}>
                      {filter.charAt(0).toUpperCase() + filter.slice(1)}
                      <span style={{ fontSize: "11px" }}> ({counts[filter]})</span>
                    </button>
                  ))}
                </div>
                {filteredBookings.length === 0 ? (
                  <div style={styles.emptyState}>
                    <p style={{ fontSize: "48px", marginBottom: "12px" }}>📭</p>
                    <p style={{ color: "white", fontWeight: "bold", marginBottom: "6px" }}>No {activeFilter} bookings</p>
                    {activeFilter === "all" && (
                      <button onClick={() => navigate("/shops")} style={styles.bookNowBtn}>Book Your First Appointment</button>
                    )}
                  </div>
                ) : (
                  filteredBookings.map((booking) => (
                    <BookingCard
                      key={booking.id}
                      booking={booking}
                      alreadyReviewed={reviewedIds.includes(booking.id)}
                      onCancel={handleCancel}
                      onReview={() => setReviewBooking(booking)}
                      onViewShop={() => navigate(`/shops/${booking.shop_id}`)}
                    />
                  ))
                )}
              </>
            )}
          </>
        )}

        {/* ── HOME TAB ── */}
        {activeTab === "home" && (
          <>
            {homeLoading ? (
              <p style={{ color: "var(--text-muted)", textAlign: "center", padding: "40px" }}>Loading...</p>
            ) : (
              <>
                <div style={styles.statsRow}>
                  <StatCard label="Total" value={homeCounts.all} color="#4caf50" />
                  <StatCard label="Upcoming" value={homeCounts.upcoming} color="#4fc3f7" />
                  <StatCard label="Completed" value={homeCounts.completed} color="#4caf50" />
                  <StatCard label="Advance Paid" value={homeCounts.all} color="#ff9800" />
                </div>
                <div style={styles.filterRow}>
                  {["all", "upcoming", "completed"].map((filter) => (
                    <button key={filter} onClick={() => setHomeFilter(filter)}
                      style={{ ...styles.filterBtn, ...(homeFilter === filter ? styles.filterBtnActiveHome : {}) }}>
                      {filter.charAt(0).toUpperCase() + filter.slice(1)}
                      <span style={{ fontSize: "11px" }}> ({homeCounts[filter]})</span>
                    </button>
                  ))}
                </div>
                {filteredHomeBookings.length === 0 ? (
                  <div style={styles.emptyState}>
                    <p style={{ fontSize: "48px", marginBottom: "12px" }}>🏠</p>
                    <p style={{ color: "white", fontWeight: "bold", marginBottom: "6px" }}>No {homeFilter} home bookings</p>
                    {homeFilter === "all" && (
                      <button onClick={() => navigate("/shops")} style={{ ...styles.bookNowBtn, background: "#4caf50" }}>
                        Book a Home Visit
                      </button>
                    )}
                  </div>
                ) : (
                  filteredHomeBookings.map((booking) => (
                    <HomeBookingCard
                      key={booking.id}
                      booking={booking}
                      alreadyReviewed={reviewedHomeIds.includes(booking.id)}
                      onReview={() => setReviewBooking(booking)}
                      onViewShop={() => navigate(`/shops/${booking.shop_id}`)}
                    />
                  ))
                )}
              </>
            )}
          </>
        )}
      </div>

      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "" })} />
    </div>
  );
}

// ── Salon Booking Card ──
function BookingCard({ booking, alreadyReviewed, onCancel, onReview, onViewShop }) {
  const canCancel = ["pending", "confirmed"].includes(booking.status);
  const canReview = booking.status === "completed" && !alreadyReviewed;

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div style={styles.shopInfo}>
          <div style={styles.shopAvatar}>✂️</div>
          <div>
            <p style={styles.shopName}>{booking.shop_name}</p>
            <p style={styles.shopAddress}>📍 {booking.address}</p>
          </div>
        </div>
        <StatusBadge status={booking.status} />
      </div>
      <div style={styles.divider} />
      <div style={styles.detailsGrid}>
        <DetailItem icon="✂️" label="Service" value={booking.service_name} />
        <DetailItem icon="📅" label="Date" value={formatDate(booking.booking_date)} />
        <DetailItem icon="🕐" label="Time" value={formatTime(booking.booking_time)} />
        <DetailItem icon="💰" label="Amount" value={`₹${booking.price}`} />
      </div>
      {canCancel && (
        <div style={styles.payReminder}>
          💡 Pay <strong>₹{booking.price}</strong> directly to Salon after service
        </div>
      )}
      {booking.status === "completed" && alreadyReviewed && (
        <div style={styles.reviewedBadge}>⭐ You reviewed this appointment</div>
      )}
      <div style={styles.cardActions}>
        <button onClick={onViewShop} style={styles.viewShopBtn}>View Shop</button>
        {canReview && <button onClick={onReview} style={styles.reviewBtn}>⭐ Write Review</button>}
        {canCancel && <button onClick={() => onCancel(booking.id)} style={styles.cancelBtn}>Cancel</button>}
        {booking.status === "completed" && <button onClick={onViewShop} style={styles.rebookBtn}>🔄 Book Again</button>}
      </div>
    </div>
  );
}

// ── Home Booking Card ──
function HomeBookingCard({ booking, alreadyReviewed, onReview, onViewShop }) {
  const remaining = parseFloat(booking.total_price || 0) - parseFloat(booking.advance_amount || 0);
  const canReview = booking.status === "completed" && !alreadyReviewed;

  return (
    <div style={{ ...styles.card, borderColor: "rgba(76,175,80,0.25)" }}>
      <div style={styles.cardHeader}>
        <div style={styles.shopInfo}>
          <div style={styles.shopAvatar}>🏠</div>
          <div>
            <p style={styles.shopName}>{booking.shop_name}</p>
            <p style={styles.shopAddress}>📍 {booking.customer_address}</p>
          </div>
        </div>
        <StatusBadge status={booking.status} />
      </div>
      <div style={styles.divider} />
      <div style={styles.detailsGrid}>
        <DetailItem icon="✂️" label="Services" value={booking.service_names} />
        <DetailItem icon="📅" label="Preferred Date" value={formatDate(booking.preferred_date)} />
        <DetailItem icon="🕐" label="Preferred Time" value={booking.preferred_time} />
        <DetailItem icon="💰" label="Total Amount" value={`₹${booking.total_price}`} />
      </div>

      {/* Payment breakdown */}
      <div style={styles.homePayBox}>
        <div style={styles.homePayRow}>
          <span style={{ color: "var(--text-muted)", fontSize: "12px" }}>✅ Advance Paid</span>
          <span style={{ color: "#4caf50", fontWeight: "bold", fontSize: "13px" }}>₹{booking.advance_amount}</span>
        </div>
        <div style={styles.homePayRow}>
          <span style={{ color: "var(--text-muted)", fontSize: "12px" }}>💡 Doorstep Payment</span>
          <span style={{ color: "#ff9800", fontWeight: "bold", fontSize: "13px" }}>₹{remaining}</span>
        </div>
      </div>

      {booking.status === "completed" && alreadyReviewed && (
        <div style={styles.reviewedBadge}>⭐ You reviewed this home visit</div>
      )}

      <div style={styles.cardActions}>
        <button onClick={onViewShop} style={styles.viewShopBtn}>View Shop</button>
        {canReview && (
          <button onClick={onReview} style={styles.reviewBtn}>⭐ Write Review</button>
        )}
        {booking.status === "completed" && (
          <button onClick={onViewShop} style={styles.rebookBtn}>🔄 Book Again</button>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = {
    pending:   { color: "#ff9800", bg: "rgba(255,152,0,0.15)",  label: "⏳ Pending" },
    confirmed: { color: "#4fc3f7", bg: "rgba(79,195,247,0.15)", label: "✅ Confirmed" },
    completed: { color: "#4caf50", bg: "rgba(76,175,80,0.15)",  label: "🎉 Completed" },
    cancelled: { color: "#e94560", bg: "rgba(233,69,96,0.15)",  label: "❌ Cancelled" },
    no_show:   { color: "#ab47bc", bg: "rgba(171,71,188,0.15)", label: "🚫 No Show" },
  };
  const c = config[status] || config.pending;
  return <span style={{ ...styles.badge, color: c.color, background: c.bg, border: `1px solid ${c.color}33` }}>{c.label}</span>;
}

function StatCard({ label, value, color }) {
  return (
    <div style={styles.statCard}>
      <p style={{ ...styles.statValue, color }}>{value}</p>
      <p style={styles.statLabel}>{label}</p>
    </div>
  );
}

function DetailItem({ icon, label, value }) {
  return (
    <div>
      <p style={styles.detailLabel}>{icon} {label}</p>
      <p style={styles.detailValue}>{value}</p>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

function formatTime(timeStr) {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":");
  if (!m) return timeStr;
  let hour = parseInt(h);
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${m} ${ampm}`;
}

const styles = {
  page: { minHeight: "100vh", background: "var(--bg-primary)" },
  header: { background: "var(--bg-secondary)", padding: "16px 20px", borderBottom: "1px solid var(--border)" },
  headerInner: { maxWidth: "700px", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" },
  title: { fontSize: "1.4rem", fontWeight: "800", color: "white", margin: 0 },
  newBtn: { background: "var(--accent)", color: "white", border: "none", padding: "10px 18px", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "bold" },
  tabBar: { maxWidth: "700px", margin: "16px auto 0", padding: "0 16px", display: "flex", gap: "10px" },
  tabBtn: { flex: 1, padding: "12px 10px", border: "1px solid var(--border)", borderRadius: "10px", background: "var(--bg-card)", cursor: "pointer", fontSize: "13px", fontWeight: "bold", color: "var(--text-muted)", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" },
  tabBtnActiveSalon: { background: "rgba(233,69,96,0.12)", borderColor: "var(--accent)", color: "var(--accent)" },
  tabBtnActiveHome: { background: "rgba(76,175,80,0.12)", borderColor: "#4caf50", color: "#4caf50" },
  tabBadgeRed: { background: "var(--accent)", color: "white", borderRadius: "20px", fontSize: "10px", padding: "2px 6px", fontWeight: "bold" },
  tabBadgeGreen: { background: "#4caf50", color: "white", borderRadius: "20px", fontSize: "10px", padding: "2px 6px", fontWeight: "bold" },
  container: { maxWidth: "700px", margin: "0 auto", padding: "16px" },
  errorBox: { background: "rgba(233,69,96,0.1)", color: "#ff6b6b", padding: "12px", borderRadius: "8px", marginBottom: "16px", border: "1px solid rgba(233,69,96,0.3)" },
  statsRow: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px", marginBottom: "16px" },
  statCard: { background: "var(--bg-card)", borderRadius: "10px", padding: "16px", textAlign: "center", border: "1px solid var(--border)" },
  statValue: { fontSize: "1.6rem", fontWeight: "bold", margin: "0 0 4px" },
  statLabel: { color: "var(--text-muted)", fontSize: "12px", margin: 0 },
  filterRow: { display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" },
  filterBtn: { padding: "8px 14px", border: "1px solid var(--border)", borderRadius: "20px", background: "var(--bg-card)", cursor: "pointer", fontSize: "12px", color: "var(--text-muted)" },
  filterBtnActiveSalon: { border: "1px solid var(--accent)", color: "var(--accent)", fontWeight: "bold", background: "rgba(233,69,96,0.1)" },
  filterBtnActiveHome: { border: "1px solid #4caf50", color: "#4caf50", fontWeight: "bold", background: "rgba(76,175,80,0.1)" },
  emptyState: { textAlign: "center", padding: "50px 20px" },
  bookNowBtn: { background: "var(--accent)", color: "white", border: "none", padding: "12px 24px", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: "bold", marginTop: "16px" },
  card: { background: "var(--bg-card)", borderRadius: "14px", padding: "18px", marginBottom: "12px", border: "1px solid var(--border)" },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "14px", flexWrap: "wrap", gap: "8px" },
  shopInfo: { display: "flex", gap: "12px", alignItems: "center" },
  shopAvatar: { fontSize: "28px" },
  shopName: { fontWeight: "bold", fontSize: "15px", margin: "0 0 3px", color: "white" },
  shopAddress: { color: "var(--text-muted)", fontSize: "12px", margin: 0 },
  badge: { padding: "4px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "bold", whiteSpace: "nowrap" },
  divider: { height: "1px", background: "var(--border)", margin: "12px 0" },
  detailsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" },
  detailLabel: { color: "var(--text-muted)", fontSize: "11px", margin: "0 0 3px" },
  detailValue: { color: "white", fontSize: "14px", fontWeight: "600", margin: 0 },
  payReminder: { background: "rgba(76,175,80,0.1)", color: "#4caf50", padding: "10px 14px", borderRadius: "8px", fontSize: "12px", marginBottom: "12px", border: "1px solid rgba(76,175,80,0.2)" },
  reviewedBadge: { background: "rgba(245,166,35,0.1)", color: "#f5a623", padding: "8px 14px", borderRadius: "8px", fontSize: "12px", marginBottom: "12px", border: "1px solid rgba(245,166,35,0.2)" },
  homePayBox: { background: "rgba(76,175,80,0.07)", border: "1px solid rgba(76,175,80,0.2)", borderRadius: "8px", padding: "10px 14px", marginBottom: "12px", display: "flex", flexDirection: "column", gap: "6px" },
  homePayRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  cardActions: { display: "flex", gap: "8px", flexWrap: "wrap" },
  viewShopBtn: { padding: "8px 14px", border: "1px solid var(--border)", borderRadius: "8px", background: "none", cursor: "pointer", fontSize: "12px", color: "var(--text-secondary)" },
  reviewBtn: { padding: "8px 14px", border: "1px solid rgba(245,166,35,0.4)", borderRadius: "8px", background: "rgba(245,166,35,0.1)", cursor: "pointer", fontSize: "12px", color: "#f5a623", fontWeight: "bold" },
  cancelBtn: { padding: "8px 14px", border: "1px solid rgba(233,69,96,0.3)", borderRadius: "8px", background: "rgba(233,69,96,0.1)", cursor: "pointer", fontSize: "12px", color: "var(--accent)" },
  rebookBtn: { padding: "8px 14px", border: "none", borderRadius: "8px", background: "var(--accent)", cursor: "pointer", fontSize: "12px", color: "white", fontWeight: "bold" },
};